// 📏 حساب سعر اللوحة - نسخة ارت تاتش (/partner-price). عرض فقط: كل الأرقام
// (results) محسوبة بـCanvasCostCalculator.jsx بنفس الدوال، وهنا تنرسم بس.
import React from "react";
import { Box, Typography } from "@mui/material";
import { PartnerShell, PageHeader, Card, Field, Suffix } from "./PartnerUI";
import { P } from "./partnerTheme";
import { filterPriceRowsBySize } from "../utils/boardPriceCalc";

// نفس ترتيب صفوف results بنسخة الشريك: كانفاس، أيل، كاس
const ROW_NAMES = [
  "لوحة مع إطار داخلي خشب فقط",
  "لوحة مع إطار داخلي خشب وإطار خارجي مودرن (L Frame)",
  "لوحة مع إطار داخلي خشب وإطار خارجي كلاسك (Kas Frame)",
];

const fmt = (n) => Math.round(Number(n) || 0).toLocaleString("en-US");

// بدون أسهم الزيادة/النقصان بحقول الأرقام
const noSpinners = {
  "& input[type=number]": { MozAppearance: "textfield" },
  "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": { WebkitAppearance: "none", margin: 0 },
};

const tableSx = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: 14.5,
  "& th, & td": { px: 2, py: 1.4, textAlign: "center" },
  "& th": { background: P.surface2, color: P.muted, fontWeight: 700, fontSize: 13.5 },
  "& tbody tr + tr td": { borderTop: `1px solid ${P.line}` },
  "& td": { fontVariantNumeric: "tabular-nums" },
};

const PriceList = ({ title, data, width, height, onPick }) => {
  const filtered = filterPriceRowsBySize(data, width, height);
  return (
    <Card sx={{ overflow: "hidden" }}>
      <Typography sx={{ fontWeight: 800, fontSize: 16, px: 2, py: 1.75, borderBottom: `1px solid ${P.line}` }}>{title}</Typography>
      {filtered.length === 0 ? (
        <Typography sx={{ color: P.muted, textAlign: "center", py: 3, fontSize: 14 }}>لا توجد قياسات مطابقة</Typography>
      ) : (
        <Box component="table" sx={tableSx}>
          <thead>
            <tr>
              <th>القياس</th>
              <th>Origin</th>
              <th>Edition</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((row, i) => (
              <Box
                component="tr"
                key={i}
                onClick={() => onPick(row[0])}
                sx={{ cursor: "pointer", "&:hover": { background: P.surface2 } }}
              >
                <td style={{ direction: "ltr", fontWeight: 700 }}>{row[0]}</td>
                <td>{row[1]}</td>
                <td>{row[2]}</td>
              </Box>
            ))}
          </tbody>
        </Box>
      )}
    </Card>
  );
};

const PartnerPriceCalculator = ({ lengthCm, widthCm, setLengthCm, setWidthCm, results, frameData, kasData }) => {
  // اختيار قياس من القائمة يعبّي الطول والعرض
  const pickSize = (size) => {
    const [w, h] = size.replace(/\s/g, "").split("×");
    setLengthCm(w);
    setWidthCm(h);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <PartnerShell maxWidth={900}>
      <PageHeader
        title="حساب سعر اللوحة"
        subtitle="ادخل قياس اللوحة أو اختار قياس لوحة من القائمة أدناه"
      />

      <Card sx={{ p: { xs: 2, md: 2.5 }, mb: 3 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: results ? 2.5 : 0, ...noSpinners }}>
          <Field
            label="الطول"
            type="number"
            value={lengthCm}
            onChange={(e) => setLengthCm(e.target.value)}
            inputProps={{ inputMode: "numeric", style: { fontSize: 18, fontWeight: 700 } }}
            InputProps={{ endAdornment: <Suffix>سم</Suffix> }}
          />
          <Field
            label="العرض"
            type="number"
            value={widthCm}
            onChange={(e) => setWidthCm(e.target.value)}
            inputProps={{ inputMode: "numeric", style: { fontSize: 18, fontWeight: 700 } }}
            InputProps={{ endAdornment: <Suffix>سم</Suffix> }}
          />
        </Box>

        {results && (
          <Box sx={{ border: `1px solid ${P.line}`, borderRadius: "12px", overflow: "hidden", overflowX: "auto" }}>
            <Box component="table" sx={{ ...tableSx, minWidth: 420 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "right" }}>النوع</th>
                  <th>Origin</th>
                  <th>Edition</th>
                </tr>
              </thead>
              <tbody>
                {results.rows.map((r, i) => (
                  <tr key={i}>
                    <td style={{ textAlign: "right", fontSize: 13.5 }}>{ROW_NAMES[i] || r.title}</td>
                    <td style={{ fontWeight: 800, whiteSpace: "nowrap" }}>{fmt(r.origin)} <span style={{ color: P.faint, fontSize: 11 }}>د.ع</span></td>
                    <td style={{ fontWeight: 800, whiteSpace: "nowrap" }}>{fmt(r.edition)} <span style={{ color: P.faint, fontSize: 11 }}>د.ع</span></td>
                  </tr>
                ))}
              </tbody>
            </Box>
          </Box>
        )}
      </Card>

      <Typography sx={{ fontWeight: 800, fontSize: 20, mb: 2 }}>قائمة الأسعار</Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2, alignItems: "start" }}>
        <PriceList title="إطار مودرن (L Frame)" data={frameData} width={lengthCm} height={widthCm} onPick={pickSize} />
        <PriceList title="إطار كلاسك (Kas Frame)" data={kasData} width={lengthCm} height={widthCm} onPick={pickSize} />
      </Box>
    </PartnerShell>
  );
};

export default PartnerPriceCalculator;
